import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * Spinner — an animated loading indicator. Design adopted from the TailGrids
 * spinner model (the system our Figma is built on), rebuilt as scalable SVG and
 * wired to First Bite tokens:
 *
 *   type   default | dotted | dotted-round   (arc ring · radial bars · radial dots)
 *   size   sm | md | lg | xl | xxl           (20 · 28 · 36 · 40 · 48 px)
 *
 * Color comes from `currentColor`, so the spinner inherits the surrounding text
 * color. The wrapper defaults to `text-fbui-basil-700` (brand); recolor by
 * passing a `text-*` token via `className` (e.g. `text-white` inside a filled
 * button, `text-fbui-gray-500` for a muted inline state).
 *
 * Accessibility: renders `role="status"` with an `aria-label` (default
 * "Loading") so assistive tech announces the loading state, plus an `sr-only`
 * text node for the same. Set `decorative` when the spinner sits inside an
 * already-labeled control (e.g. a button whose text says "Saving…") — that
 * drops the role and hides it from the accessibility tree to avoid double
 * announcement. The animation slows (rather than stops) under
 * `prefers-reduced-motion` so it still reads as active.
 */
const spinnerVariants = cva('inline-block shrink-0 text-fbui-basil-700', {
  variants: {
    size: {
      sm: 'size-5',
      md: 'size-7',
      lg: 'size-9',
      xl: 'size-10',
      xxl: 'size-12',
    },
  },
  defaultVariants: { size: 'md' },
})

/**
 * Spin animation per type, slowed (not stopped) under reduced-motion so it still
 * reads as active. The dotted variants use `steps(8)` for a ticking rotation —
 * the whole `animation` shorthand is set in one declaration (rather than layering
 * a separate `animation-timing-function`) so the timing function reliably wins.
 */
const spinClass = {
  smooth:
    'motion-safe:animate-spin motion-reduce:[animation:spin_2.4s_linear_infinite]',
  stepped:
    'motion-safe:[animation:spin_1s_steps(8)_infinite] motion-reduce:[animation:spin_2.4s_steps(8)_infinite]',
} as const

/** Evenly-spaced spokes around the 24×24 viewBox, opacity trailing off the leader. */
const SPOKES = Array.from({ length: 8 }, (_, i) => ({
  rotate: i * 45,
  opacity: 0.25 + (i / 7) * 0.75,
}))

export interface SpinnerProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    VariantProps<typeof spinnerVariants> {
  /** Visual style. `default` = arc ring, `dotted` = radial bars, `dotted-round` = radial dots. */
  type?: 'default' | 'dotted' | 'dotted-round'
  /** Accessible label announced by screen readers. Defaults to "Loading". */
  label?: string
  /**
   * Hide from assistive tech and drop `role="status"`. Use when the spinner is
   * inside an already-labeled control (e.g. a button reading "Saving…").
   */
  decorative?: boolean
}

export const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(
  (
    {
      className,
      size,
      type = 'default',
      label = 'Loading',
      decorative,
      ...props
    },
    ref
  ) => {
    const a11y = decorative
      ? ({ 'aria-hidden': true } as const)
      : ({ role: 'status', 'aria-label': label } as const)

    return (
      <span
        ref={ref}
        className={cn(spinnerVariants({ size, className }))}
        {...a11y}
        {...props}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={cn(
            'size-full',
            type === 'default' ? spinClass.smooth : spinClass.stepped
          )}
          aria-hidden="true"
        >
          {type === 'default' && (
            <>
              <circle
                cx="12"
                cy="12"
                r="9.5"
                stroke="currentColor"
                strokeOpacity="0.25"
                strokeWidth="3"
              />
              <path
                d="M12 2.5a9.5 9.5 0 0 1 9.5 9.5"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </>
          )}
          {type === 'dotted' &&
            SPOKES.map(({ rotate, opacity }) => (
              <rect
                key={rotate}
                x="10.75"
                y="2"
                width="2.5"
                height="6"
                rx="1.25"
                fill="currentColor"
                opacity={opacity}
                transform={`rotate(${rotate} 12 12)`}
              />
            ))}
          {type === 'dotted-round' &&
            SPOKES.map(({ rotate, opacity }) => (
              <circle
                key={rotate}
                cx="12"
                cy="4"
                r="1.75"
                fill="currentColor"
                opacity={opacity}
                transform={`rotate(${rotate} 12 12)`}
              />
            ))}
        </svg>
        {!decorative && <span className="sr-only">{label}</span>}
      </span>
    )
  }
)
Spinner.displayName = 'Spinner'
